import React, { FC } from 'react'

import isEmpty from 'lodash.isempty'

import {
  CardActionArea,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  Box
} from '@material-ui/core'
import { CardActionAreaProps } from '@material-ui/core/CardActionArea'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import { Skeleton } from '@material-ui/lab'

import ExtendedButton from '~/components/shared/ExtendedButton'
import Text from '~/components/shared/Text'

import theme from '~/theme'

import { usePackageCardStyles } from './packageCard.styles'
import PackageHeaderCard from './PackageHeaderCard'

interface PackageCardProps extends CardActionAreaProps {
  features: { label: string; allowed: boolean }[]
  title: string
  price: number
  currency?: string
  loading?: boolean
  disableFeatures?: boolean
  buttonText?: string
  packageType?: string
}

const placeholderFeatures = new Array(3).fill({})

const PackageCard: FC<PackageCardProps> = ({
  onClick,
  features,
  title,
  price,
  loading,
  disableFeatures,
  buttonText,
  packageType,
  ...rest
}) => {
  const classes = usePackageCardStyles()

  const currentFeatures = loading
    ? placeholderFeatures
    : isEmpty(features)
    ? []
    : features

  const isPlus = title === 'Plus'
  const isPrivate = packageType === 'private'
  const helperText = isPrivate ? ' per listing' : 'per week incl GST'

  return (
    <Box position="relative">
      {isPlus && (
        <Box className={classes.popular}>
          <Text fontSize={14} textAlign="center" mt={0.5}>
            Most Popular
          </Text>
        </Box>
      )}

      <Card
        className={classes.root}
        style={{ pointerEvents: loading ? 'none' : 'auto' }}
      >
        <CardActionArea
          onClick={onClick}
          className={classes.actionArea}
          {...rest}
        >
          <Grid container spacing={6}>
            <Grid
              item
              xs={12}
              style={{
                backgroundColor: isPlus
                  ? theme.palette.bg.primary.main
                  : theme.palette.common.white
              }}
            >
              <PackageHeaderCard
                loading={loading}
                title={title}
                price={price}
                helperText={helperText}
                size="medium"
              />
            </Grid>
            {!disableFeatures && !!currentFeatures.length && (
              <Grid item xs={12}>
                <List dense style={{ minHeight: 260 }}>
                  {currentFeatures.map((feature) => {
                    const isBoldLabel =
                      feature.label === 'Listing live until sold'

                    const words = isBoldLabel
                      ? feature.label
                      : feature.label?.split(' ')

                    const boldWords = ['high', 'higher']

                    return (
                      <ListItem key={feature.label}>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primaryTypographyProps={{
                            color: feature.allowed
                              ? 'textPrimary'
                              : 'textSecondary'
                          }}
                          primary={
                            loading ? (
                              <Skeleton width="100%" />
                            ) : (
                              <Text>
                                {typeof words === 'string' ? (
                                  <Text component="span" fontWeight={700}>
                                    {words}{' '}
                                  </Text>
                                ) : (
                                  words.map((word, index) => {
                                    const isBoldWord = boldWords.includes(word)

                                    return (
                                      <Text
                                        component="span"
                                        fontWeight={isBoldWord ? 700 : 400}
                                        key={index}
                                      >
                                        {word}{' '}
                                      </Text>
                                    )
                                  })
                                )}
                              </Text>
                            )
                          }
                        />
                      </ListItem>
                    )
                  })}
                </List>
              </Grid>
            )}
            <Grid item xs={12}>
              <Box display="flex">
                <ExtendedButton
                  mx={2}
                  fullWidth
                  variant="outlined"
                  className="continue-btn"
                >
                  {buttonText || 'Continue'}
                </ExtendedButton>
              </Box>
            </Grid>
          </Grid>
        </CardActionArea>
      </Card>
    </Box>
  )
}

export default PackageCard
